/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import _ from 'lodash'
import { WF_STEP_PROCESSING_TYPE, WF_STEP_TYPE, RA_ACTION_TYPE } from '../../../constants/workflow-steps'
import { isValidFilename as isValid } from '../../browser/add-document/add-new-document/FormValidation'

// 1. TypeScript Augmentation to fix "is not a function" errors
declare module 'yup' {
  interface MixedSchema<TType = any, TContext = any, TDefault = any, TFlags = any> {
    isValid(): this
    isNotEmpty(): this
  }
}

// 2. Register Methods BEFORE creating the schema
yup.addMethod(yup.mixed, 'isNotEmpty', function (this: any) {
  return this.test('isNotEmpty', 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR', function (value: any) {
    if (!value || value === '') return false
    if (_.isArray(value) && !value.length) return false
    return true
  })
})

yup.addMethod(yup.mixed, 'isValid', function (this: any) {
  return this.test('isValid', 'FILENAME_CONTAINS_INVALID_CHARACTERS', function (value: any) {
    return isValid(value)
  })
})

const ValidationSchema = () => {
  yup.setLocale({
    mixed: {
      required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    }
  })

  const someAtOnceErrorMessage = 'The # of users cannot be empty or set to 0, it must be set to 1 or more'

  // Reusable Sub-schemas
  const taskTeamStepLabel = yup
    .object()
    .shape({
      id: yup.number().nullable(),
      label: yup.string().nullable(),
      value: yup.number().nullable()
    })
    .nullable()
    // FIX: Catch empty strings from the UI and convert them to null
    .transform((value: any, originalValue: any) => {
      if (originalValue === '') return null
      return value
    })
    .default(null)

  const nofAllValue = yup
    .mixed()
    .nullable()
    .when('processingType', {
      is: WF_STEP_PROCESSING_TYPE.N_OF_ALL,
      // FIX: Use callback syntax for then
      then: () =>
        yup
          .number()
          .transform((value: any) => (isNaN(value) ? undefined : value))
          .min(1, someAtOnceErrorMessage)
          .required(someAtOnceErrorMessage),
      otherwise: (schema: any) => schema.nullable()
    })

  return yup.object().shape({
    fileName: yup
      .string()
      .trim()
      .max(255)
      .test(isValid, 'FILENAME_CONTAINS_INVALID_CHARACTERS', function (value: any) {
        return isValid(value)
      })
      .when(['fileToShow', 'docType'], {
        // FIX: destructure arguments in is callback
        is: (fileToShow: any, docType: any) => {
          return fileToShow?.type === 'PLACEHOLDER' && docType?.autonamingEnabled
        },
        then: (schema: any) => schema.required(),
        otherwise: (schema: any) => schema.nullable()
      })
      .default(''),

    expirationDate: yup
      .object()
      .nullable()
      .when('docType.requireExpirationDate', {
        is: true,
        then: (schema: any) => schema.required()
      }),

    justificationTextId: yup
      .number()
      .min(0)
      .nullable()
      .when('docType.requireJustification', {
        is: true,
        then: (schema: any) => schema.required()
      }),

    crApprovers: yup.object().shape({
      initialApprovers: yup.object().shape({
        type: yup.string().oneOf(_.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default([])
      })
    }),

    editors: yup.object().shape({
      collaborators: yup.object().shape({
        type: yup.string().oneOf(_.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.USE_INITIATOR),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue,
              merged: yup.array().min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR').required()
            })
          )
          .when('type', {
            is: WF_STEP_TYPE.USE_PREDEFINED,
            then: (schema: any) => schema.required()
          })
          .when('type', {
            is: WF_STEP_TYPE.SETUP_WITH_WORKFLOW,
            then: (schema: any) => schema.required().min(1)
          })
          .nullable()
          .default([])
      })
    }),

    // Note: Apply similar 'then: (schema) => schema' fixes to reviewers and approvers sections
    reviewers: yup.object().shape({
      reviewers: yup.object().shape({
        type: yup.string().oneOf(_.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default([])
      })
    }),

    approvers: yup.object().shape({
      approvers: yup.object().shape({
        type: yup.string().oneOf(_.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.NOT_DEFINED),
        steps: yup
          .array()
          .of(
            yup.object().shape({
              taskTeamStepLabel,
              processingType: yup.string().nullable().required(),
              nofAllValue,
              merged: yup.array()
            })
          )
          .nullable()
          .default([])
      })
    }),

    rejectionReviewers: yup.object().shape({
      type: yup.string().oneOf(_.values(WF_STEP_TYPE)).default(WF_STEP_TYPE.USE_INITIATOR),
      usersGroups: yup
        .array()
        .when('type', {
          is: WF_STEP_TYPE.USE_PREDEFINED,
          then: (schema: any) => schema.required().min(1)
        })
        .default([])
        .nullable()
    })
  })
}

export default ValidationSchema
